// SPDX-License-Identifier: LicenseRef-Proprietary

import React, { useState } from 'react';

interface ExportImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: () => void | Promise<void>;
  title: string;
  message: string;
  /**
   * Action verb used in the in-flight label. Defaults inferred from `title`
   * (contains "Import" → "Importing"; otherwise "Exporting").
   */
  actionVerb?: 'Exporting' | 'Importing';
}

export const ExportImportModal: React.FC<ExportImportModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  title,
  message,
  actionVerb,
}) => {
  // Track in-flight state so the user can't double-click or close the modal
  // mid-action. We support both sync and async `onSelect` handlers.
  const isImportContext = /import/i.test(title);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [isImporting, setIsImporting] = useState<boolean>(false);
  const inFlight = isExporting || isImporting;

  const verb = actionVerb ?? (isImportContext ? 'Importing' : 'Exporting');

  if (!isOpen) return null;

  const handleSelect = async () => {
    if (inFlight) return;
    const setBusy = isImportContext ? setIsImporting : setIsExporting;
    setBusy(true);
    try {
      await onSelect();
    } finally {
      // Modal may already be unmounted by the parent (which historically closes
      // the modal as part of its select handler). The setBusy call is then a
      // no-op on the unmounted instance — that's fine; React will warn only in
      // strict-mode dev builds, not in production.
      setBusy(false);
    }
  };

  const handleBackdropClick = () => {
    if (inFlight) return;
    onClose();
  };

  const handleCancel = () => {
    if (inFlight) return;
    onClose();
  };

  return (
    <div
      className="dashboard-settings-modal__backdrop"
      onClick={handleBackdropClick}
      role="presentation"
    >
      <div
        className="dashboard-settings-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="dashboard-settings-modal__header">
          <h3 className="dashboard-settings-modal__title">{title}</h3>
          <button
            onClick={handleCancel}
            className="dashboard-settings-modal__close"
            aria-label="Close modal"
            disabled={inFlight}
          >
            ×
          </button>
        </div>
        <p className="dashboard-settings-modal__description">{message}</p>

        <div className="dashboard-settings-modal__footer">
          <button
            onClick={handleCancel}
            className="btn btn-secondary"
            disabled={inFlight}
          >
            Cancel
          </button>
          <button
            onClick={() => handleSelect()}
            className="btn btn-primary"
            disabled={inFlight}
            aria-busy={inFlight}
          >
            {inFlight ? `${verb}...` : (isImportContext ? 'Import' : 'Export')}
          </button>
        </div>
      </div>
    </div>
  );
};
